import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import type {
  Stavba,
  StavbaCreate,
  Company,
  CompanyCreate,
  CompanyContact,
  ContactCreate,
  CompanyRole,
  DenikEntry,
  DenikCreatePayload,
  DenikUpdatePayload,
  DenikKontaktyMap,
  DenikWorkerFav,
  DenikWorkerFavCreate,
  DenikAttachment,
  Vada,
  VadyStats,
  VadaUpdatePayload,
  Priorita,
  Rfi,
  RfiKategorie,
  RfiUpdatePayload,
  KontrolniDen,
  DocTemplate,
  DashboardOverview,
  User,
  AresDetail,
  AresSearchItem,
  Harmonogram,
  HarmonogramListItem,
  HarmonogramCreate,
  HarmonogramTask,
  HarmonogramTaskCreate,
  SavedDocument,
  SavedDocumentCreate,
  StavbaPhoto,
  ProjectFile,
  ProjectFileStats,
  ProjectFileShare,
  ProjectFilePublicLink,
  SharedProjectFile,
  ZmenovyList,
  ZmenovyListStats,
  ZmenovyListCreatePayload,
  ZmenovyListUpdatePayload,
  VyuctovaniPolozka,
  VyuctovaniPolozkaCreate,
  VyuctovaniPolozkaUpdate,
  VyuctovaniShare,
  SharedVyuctovani,
  KdShare,
  SharedKd,
  VyuctovaniSouhrn,
  VyuctovaniVklad,
  VyuctovaniPrevod,
  VyuctovaniPrevodCreate,
  NotifikaceNastaveni,
  ZamestnanecKarta,
  ZamestnanecKartaCreate,
  ZamestnanecKartaUpdate,
  ZamestnanecPolozka,
  ZamestnanecPolozkaCreate,
  ZamestnanecPolozkaUpdate,
  ZamestnanecKartaShare,
  Ukol,
  UkolCreate,
  UkolUpdate,
  UkolStav,
  PoznamkaKarta,
  PoznamkaKartaCreate,
  PoznamkaKartaUpdate,
  Vehicle,
  VehicleCreate,
  VehicleUpdate,
  LogbookMonth,
  LogbookMonthListItem,
  LogbookMonthCreate,
  LogbookMonthUpdate,
  Logo,
  LogoCreate,
  KosilkaSchvaleni,
  KosilkaSchvaleniCreate,
  ApproverEmail,
  Podpis,
  PodpisCreate,
  PodepsanyDokument,
} from './schemas';

/**
 * TanStack Query klíče — centralizováno kvůli invalidacím.
 */
export const qk = {
  me: ['me'] as const,
  stavby: ['stavby'] as const,
  stavba: (id: number) => ['stavby', id] as const,
  companies: (role?: CompanyRole) => ['companies', role ?? 'all'] as const,
  emailRecipients: ['email', 'recipients'] as const,
  denik: (stavbaId: number) => ['stavby', stavbaId, 'denik'] as const,
  denikEntry: (stavbaId: number, denikId: number) =>
    ['stavby', stavbaId, 'denik', denikId] as const,
  denikKontakty: (stavbaId: number) => ['stavby', stavbaId, 'denik-kontakty'] as const,
  denikPhotos: (stavbaId: number, denikId: number) =>
    ['stavby', stavbaId, 'denik', denikId, 'photos'] as const,
  denikAttachments: (stavbaId: number, denikId: number) =>
    ['stavby', stavbaId, 'denik', denikId, 'attachments'] as const,
  denikWorkers: (stavbaId: number) => ['stavby', stavbaId, 'denik-workers'] as const,
  vady: (stavbaId: number, filters?: Record<string, string>) =>
    ['stavby', stavbaId, 'vady', filters ?? {}] as const,
  vadyStats: (stavbaId: number) => ['stavby', stavbaId, 'vady', 'stats'] as const,
  rfi: (stavbaId: number) => ['stavby', stavbaId, 'rfi'] as const,
  kontrolniDny: (stavbaId: number) => ['stavby', stavbaId, 'kd'] as const,
  kdShares: ['kd', 'shares'] as const,
  kdSharedWithMe: ['kd', 'shared-with-me'] as const,
  templates: (docType?: string) => ['templates', docType ?? 'all'] as const,
  dashboard: ['dashboard'] as const,
  ares: (ico: string) => ['ares', ico] as const,
  aresSearch: (q: string) => ['ares-search', q] as const,
  harmonogramy: (stavbaId: number) => ['stavby', stavbaId, 'harmonogramy'] as const,
  harmonogram: (stavbaId: number, hid: number) => ['stavby', stavbaId, 'harmonogramy', hid] as const,
  documents: (filters?: Record<string, string | number | undefined>) =>
    ['documents', filters ?? {}] as const,
  document: (id: number) => ['documents', id] as const,
  photos: (stavbaId: number, kategorie?: string) =>
    ['stavby', stavbaId, 'photos', kategorie ?? 'all'] as const,
  projectDocs: (stavbaId: number, kategorie?: string) =>
    ['stavby', stavbaId, 'project-docs', kategorie ?? 'all'] as const,
  zmenoveListy: (stavbaId: number) => ['stavby', stavbaId, 'zmenove-listy'] as const,
  zmenoveListyStats: (stavbaId: number) => ['stavby', stavbaId, 'zmenove-listy', 'stats'] as const,
  vyuctovani: ['vyuctovani'] as const,
  vyuctovaniShares: ['vyuctovani', 'shares'] as const,
  vyuctovaniSharedWithMe: ['vyuctovani', 'shared-with-me'] as const,
  vyuctovaniSouhrn: ['vyuctovani', 'souhrn'] as const,
  kosilkyKeSchvaleni: ['kosilky', 'ke-schvaleni'] as const,
  kosilkyOdeslane: ['kosilky', 'odeslane'] as const,
  kosilkySchvalovatele: ['kosilky', 'schvalovatele'] as const,
  notifikaceNastaveni: ['notifikace', 'nastaveni'] as const,
  zamestnanci: ['zamestnanci'] as const,
  zamestnanec: (id: number) => ['zamestnanci', id] as const,
  zamestnanciSharedWithMe: ['zamestnanci', 'shared-with-me'] as const,
  zamestnanecShares: (id: number) => ['zamestnanci', id, 'shares'] as const,
  ukolyZadane: ['ukoly', 'zadane'] as const,
  ukolyMoje: ['ukoly', 'moje'] as const,
  poznamky: ['poznamky'] as const,
  vehicles: ['vehicles'] as const,
  logbookMonths: (vid: number) => ['vehicles', vid, 'months'] as const,
  logbookMonth: (vid: number, year: number, month: number) =>
    ['vehicles', vid, 'months', year, month] as const,
  logos: ['logos'] as const,
  activeLogo: ['logos', 'active'] as const,
  podpisy: ['podpisy'] as const,
  podepsane: ['podepsane-dokumenty'] as const,
};

// === Me ===
export function useMe(options?: Partial<UseQueryOptions<User>>) {
  return useQuery<User>({
    queryKey: qk.me,
    queryFn: () => api.get<User>('/auth/me'),
    ...options,
  });
}

// === Stavby ===
export function useStavby() {
  return useQuery<Stavba[]>({
    queryKey: qk.stavby,
    queryFn: () => api.get<Stavba[]>('/stavby/'),
  });
}

export function useCreateStavba() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: StavbaCreate) => api.post<Stavba>('/stavby/', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.stavby }),
  });
}

export function useUpdateStavba() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: StavbaCreate }) =>
      api.put<Stavba>(`/stavby/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.stavby }),
  });
}

export function useDeleteStavba() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/stavby/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.stavby }),
  });
}

// === Companies ===
export function useCompanies(role?: CompanyRole) {
  return useQuery<Company[]>({
    queryKey: qk.companies(role),
    queryFn: () => api.get<Company[]>('/companies/', { query: role ? { role } : undefined }),
  });
}

export interface EmailRecipientHistory {
  email: string;
  /** 0 = adresa jen ze společného firemního seznamu, zatím nepoužitá. */
  use_count: number;
  last_used: string | null;
  /** Jméno ze společného firemního seznamu (doména účtu), jinak ''. */
  name?: string;
  /** Adresa je ve společném seznamu pro doménu účtu (např. @kvs-stavebni.cz). */
  domain?: boolean;
}

/** Historie adres, na které uživatel posílal e-maily (našeptávač Komu/CC/BCC). */
export function useEmailRecipients(enabled = true) {
  return useQuery<EmailRecipientHistory[]>({
    queryKey: qk.emailRecipients,
    queryFn: () => api.get<EmailRecipientHistory[]>('/email/recipients'),
    enabled,
    staleTime: 60_000,
  });
}

export function useForgetEmailRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      api.delete<{ ok: boolean }>('/email/recipients', { query: { email } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.emailRecipients }),
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CompanyCreate) => api.post<Company>('/companies/', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });
}

/** Upravit firmu v adresáři (PUT /companies/{id}). Posílá celý objekt — vynechaná pole by se jinak vynulovala. */
export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CompanyCreate }) =>
      api.put<Company>(`/companies/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });
}

/** Smazat firmu z adresáře (DELETE /companies/{id}). */
export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/companies/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });
}

/** Přidat kontaktní osobu k firmě v adresáři (POST /companies/{id}/contacts). */
export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, data }: { companyId: number; data: ContactCreate }) =>
      api.post<CompanyContact>(`/companies/${companyId}/contacts`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  });
}

// === ARES ===
/** Přesné vyhledání podle IČO — vrací plný detail (adresa, PSČ, DIČ). */
export function useAresLookup(ico: string | null, options?: { enabled?: boolean }) {
  const cleaned = (ico ?? '').replace(/\s+/g, '');
  const valid = /^\d{1,8}$/.test(cleaned);
  return useQuery<AresDetail>({
    queryKey: qk.ares(cleaned),
    queryFn: () => api.get<AresDetail>(`/ares/${cleaned}`),
    enabled: (options?.enabled ?? true) && valid && cleaned.length >= 6,
    retry: false,
  });
}

/** Našeptávač podle jména firmy (min. 2 znaky). */
export function useAresSearch(q: string) {
  const trimmed = q.trim();
  return useQuery<AresSearchItem[]>({
    queryKey: qk.aresSearch(trimmed),
    queryFn: () => api.get<AresSearchItem[]>('/ares/search', { query: { q: trimmed } }),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });
}

// === Deník ===
export function useDenik(stavbaId: number | null) {
  return useQuery<DenikEntry[]>({
    queryKey: stavbaId ? qk.denik(stavbaId) : ['denik-inactive'],
    queryFn: () => api.get<DenikEntry[]>(`/stavby/${stavbaId}/denik`),
    enabled: !!stavbaId,
  });
}

/** Detail jednoho záznamu (obsahuje data_json). */
export function useDenikEntry(stavbaId: number | null, denikId: number | null) {
  return useQuery<DenikEntry>({
    queryKey:
      stavbaId && denikId ? qk.denikEntry(stavbaId, denikId) : ['denik-entry-inactive'],
    queryFn: () => api.get<DenikEntry>(`/stavby/${stavbaId}/denik/${denikId}`),
    enabled: !!stavbaId && !!denikId,
  });
}

/** Vytvořit nový den (POST /stavby/{sid}/denik). */
export function useCreateDenik(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DenikCreatePayload) =>
      api.post<DenikEntry>(`/stavby/${stavbaId}/denik`, payload),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: qk.denik(stavbaId) });
    },
  });
}

/** Aktualizovat zápis (PUT /stavby/{sid}/denik/{did}). */
export function useUpdateDenik(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ denikId, payload }: { denikId: number; payload: DenikUpdatePayload }) =>
      api.put<DenikEntry>(`/stavby/${stavbaId}/denik/${denikId}`, payload),
    onSuccess: (_data, vars) => {
      if (stavbaId) {
        qc.invalidateQueries({ queryKey: qk.denik(stavbaId) });
        qc.invalidateQueries({ queryKey: qk.denikEntry(stavbaId, vars.denikId) });
      }
    },
  });
}

/** Uzamknout / odemknout zápis. */
export function useLockDenik(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ denikId, lock }: { denikId: number; lock: boolean }) =>
      api.post<{ ok: boolean }>(
        `/stavby/${stavbaId}/denik/${denikId}/${lock ? 'lock' : 'unlock'}`,
        {},
      ),
    onSuccess: (_data, vars) => {
      if (stavbaId) {
        qc.invalidateQueries({ queryKey: qk.denik(stavbaId) });
        qc.invalidateQueries({ queryKey: qk.denikEntry(stavbaId, vars.denikId) });
      }
    },
  });
}

/** Kontakty Oddíl A (investor, zhotovitel, TDS, autor. dozor, BOZP). */
export function useDenikKontakty(stavbaId: number | null) {
  return useQuery<DenikKontaktyMap>({
    queryKey: stavbaId ? qk.denikKontakty(stavbaId) : ['denik-kontakty-inactive'],
    queryFn: () => api.get<DenikKontaktyMap>(`/stavby/${stavbaId}/denik-kontakty`),
    enabled: !!stavbaId,
  });
}

/** Uložit kontakty (PUT /stavby/{sid}/denik-kontakty). */
export function useSaveDenikKontakty(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DenikKontaktyMap) =>
      api.put<{ ok: boolean }>(`/stavby/${stavbaId}/denik-kontakty`, data),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: qk.denikKontakty(stavbaId) });
    },
  });
}

/** Kopírovat obsah z předchozího dne (POST /denik/{did}/copy-previous). */
export function useCopyPreviousDenik(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (denikId: number) =>
      api.post<DenikEntry>(`/stavby/${stavbaId}/denik/${denikId}/copy-previous`, {}),
    onSuccess: (_data, denikId) => {
      if (stavbaId) {
        qc.invalidateQueries({ queryKey: qk.denik(stavbaId) });
        qc.invalidateQueries({ queryKey: qk.denikEntry(stavbaId, denikId) });
      }
    },
  });
}

/** Odeslat zápis ke schválení e-mailem (POST /denik/{did}/send-approval). */
export function useSendDenikApproval(stavbaId: number | null) {
  return useMutation({
    mutationFn: (denikId: number) =>
      api.post<{ ok: boolean }>(`/stavby/${stavbaId}/denik/${denikId}/send-approval`, {}),
  });
}

/** Fotky vázané na konkrétní zápis (GET /denik/{did}/photos). */
export function useDenikPhotos(stavbaId: number | null, denikId: number | null) {
  return useQuery<StavbaPhoto[]>({
    queryKey:
      stavbaId && denikId ? qk.denikPhotos(stavbaId, denikId) : ['denik-photos-inactive'],
    queryFn: () => api.get<StavbaPhoto[]>(`/stavby/${stavbaId}/denik/${denikId}/photos`),
    enabled: !!stavbaId && !!denikId,
  });
}

/** Nahrát fotku přímo do dne (POST /denik/{did}/photos/upload). */
export function useUploadDenikPhoto(stavbaId: number | null, denikId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      form.append('popis', '');
      return api.post<StavbaPhoto>(
        `/stavby/${stavbaId}/denik/${denikId}/photos/upload`,
        form,
      );
    },
    onSuccess: () => {
      if (stavbaId && denikId) {
        qc.invalidateQueries({ queryKey: qk.denikPhotos(stavbaId, denikId) });
        qc.invalidateQueries({ queryKey: qk.denik(stavbaId) });
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'photos'] });
      }
    },
  });
}

/** Přílohy denního zápisu (PDF, dokumenty…). */
export function useDenikAttachments(stavbaId: number | null, denikId: number | null) {
  return useQuery<DenikAttachment[]>({
    queryKey:
      stavbaId && denikId ? qk.denikAttachments(stavbaId, denikId) : ['denik-attachments-inactive'],
    queryFn: () =>
      api.get<DenikAttachment[]>(`/stavby/${stavbaId}/denik/${denikId}/attachments`),
    enabled: !!stavbaId && !!denikId,
  });
}

/** Nahrát přílohu (PDF, dokument, obrázek) k dennímu zápisu. */
export function useUploadDenikAttachment(stavbaId: number | null, denikId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.post<DenikAttachment>(
        `/stavby/${stavbaId}/denik/${denikId}/attachments`,
        form,
      );
    },
    onSuccess: () => {
      if (stavbaId && denikId) {
        qc.invalidateQueries({ queryKey: qk.denikAttachments(stavbaId, denikId) });
      }
    },
  });
}

/** Smazat přílohu denního zápisu. */
export function useDeleteDenikAttachment(stavbaId: number | null, denikId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attId: number) =>
      api.delete<void>(`/stavby/${stavbaId}/denik/${denikId}/attachments/${attId}`),
    onSuccess: () => {
      if (stavbaId && denikId) {
        qc.invalidateQueries({ queryKey: qk.denikAttachments(stavbaId, denikId) });
      }
    },
  });
}

/** Geokódovat adresu stavby — vrátí lat/lon (cachuje se na serveru). */
export function useGeocodeStavba(stavbaId: number | null) {
  return useMutation({
    mutationFn: () =>
      api.post<{ latitude: string; longitude: string; cached: boolean }>(
        `/stavby/${stavbaId}/geocode`,
        {},
      ),
  });
}

/** Oblíbení pracovníci stavby (GET /denik-workers). */
export function useDenikWorkers(stavbaId: number | null) {
  return useQuery<DenikWorkerFav[]>({
    queryKey: stavbaId ? qk.denikWorkers(stavbaId) : ['denik-workers-inactive'],
    queryFn: () => api.get<DenikWorkerFav[]>(`/stavby/${stavbaId}/denik-workers`),
    enabled: !!stavbaId,
  });
}

/** Přidat oblíbeného pracovníka (POST /denik-workers). */
export function useAddDenikWorker(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DenikWorkerFavCreate) =>
      api.post<DenikWorkerFav>(`/stavby/${stavbaId}/denik-workers`, payload),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: qk.denikWorkers(stavbaId) });
    },
  });
}

/** Odebrat oblíbeného pracovníka (DELETE /denik-workers/{wid}). */
export function useRemoveDenikWorker(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workerId: number) =>
      api.delete<void>(`/stavby/${stavbaId}/denik-workers/${workerId}`),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: qk.denikWorkers(stavbaId) });
    },
  });
}

// === Vady ===
export function useVady(stavbaId: number | null, filters?: { stav?: string; priorita?: string }) {
  return useQuery<Vada[]>({
    queryKey: stavbaId ? qk.vady(stavbaId, filters as Record<string, string>) : ['vady-inactive'],
    queryFn: () =>
      api.get<Vada[]>(`/stavby/${stavbaId}/vady`, {
        query: filters,
      }),
    enabled: !!stavbaId,
  });
}

export function useVadyStats(stavbaId: number | null) {
  return useQuery<VadyStats>({
    queryKey: stavbaId ? qk.vadyStats(stavbaId) : ['vady-stats-inactive'],
    queryFn: () => api.get<VadyStats>(`/stavby/${stavbaId}/vady/stats`),
    enabled: !!stavbaId,
  });
}

export interface VadaCreatePayload {
  popis: string;
  misto?: string;
  odpovedna_osoba?: string;
  termin_odstraneni?: string;
  priorita?: Priorita;
  poznamka?: string;
  datum_zjisteni?: string;
}

/** Vytvořit novou vadu (POST /stavby/{sid}/vady). */
export function useCreateVada(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: VadaCreatePayload) =>
      api.post<Vada>(`/stavby/${stavbaId}/vady`, payload),
    onSuccess: () => {
      if (stavbaId) {
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'vady'] });
      }
    },
  });
}

/** Detail vady (GET /vady/{id}). */
export function useVada(stavbaId: number | null, vadaId: number | null) {
  return useQuery<Vada>({
    queryKey:
      stavbaId && vadaId ? ['stavby', stavbaId, 'vada', vadaId] : ['vada-inactive'],
    queryFn: () => api.get<Vada>(`/stavby/${stavbaId}/vady/${vadaId}`),
    enabled: !!stavbaId && !!vadaId,
  });
}

/** Upravit vadu (PUT /vady/{id}) — změna stavu / priority / popis / poznámka. */
export function useUpdateVada(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vadaId, payload }: { vadaId: number; payload: VadaUpdatePayload }) =>
      api.put<Vada>(`/stavby/${stavbaId}/vady/${vadaId}`, payload),
    onSuccess: (_data, vars) => {
      if (stavbaId) {
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'vady'] });
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'vada', vars.vadaId] });
      }
    },
  });
}

/** Smazat vadu. */
export function useDeleteVada(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vadaId: number) => api.delete<void>(`/stavby/${stavbaId}/vady/${vadaId}`),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'vady'] });
    },
  });
}

/** Fotky vázané na vadu. */
export function useVadaPhotos(stavbaId: number | null, vadaId: number | null) {
  return useQuery<StavbaPhoto[]>({
    queryKey:
      stavbaId && vadaId
        ? ['stavby', stavbaId, 'vada', vadaId, 'photos']
        : ['vada-photos-inactive'],
    queryFn: () => api.get<StavbaPhoto[]>(`/stavby/${stavbaId}/vady/${vadaId}/photos`),
    enabled: !!stavbaId && !!vadaId,
  });
}

/** Nahrát fotku k vadě. */
export function useUploadVadaPhoto(stavbaId: number | null, vadaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      form.append('popis', '');
      return api.post<StavbaPhoto>(`/stavby/${stavbaId}/vady/${vadaId}/photos`, form);
    },
    onSuccess: () => {
      if (stavbaId && vadaId) {
        qc.invalidateQueries({
          queryKey: ['stavby', stavbaId, 'vada', vadaId, 'photos'],
        });
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'vady'] });
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'photos'] });
      }
    },
  });
}

// === RFI ===
export function useRfi(stavbaId: number | null) {
  return useQuery<Rfi[]>({
    queryKey: stavbaId ? qk.rfi(stavbaId) : ['rfi-inactive'],
    queryFn: () => api.get<Rfi[]>(`/stavby/${stavbaId}/rfi`),
    enabled: !!stavbaId,
  });
}

export interface RfiCreatePayload {
  predmet: string;
  popis?: string;
  kategorie?: RfiKategorie;
  priorita?: Priorita;
  adresat?: string;
  odkaz_vykres?: string;
  termin_odpovedi?: string;
  dopad_harmonogram?: string;
  dopad_naklady?: string;
  poznamka?: string;
}

/** Vytvořit nové RFI (POST /stavby/{sid}/rfi). */
export function useCreateRfi(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RfiCreatePayload) =>
      api.post<Rfi>(`/stavby/${stavbaId}/rfi`, payload),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: qk.rfi(stavbaId) });
    },
  });
}

/** Detail RFI (GET /rfi/{id}). */
export function useRfiDetail(stavbaId: number | null, rfiId: number | null) {
  return useQuery<Rfi>({
    queryKey: stavbaId && rfiId ? ['stavby', stavbaId, 'rfi', rfiId] : ['rfi-detail-inactive'],
    queryFn: () => api.get<Rfi>(`/stavby/${stavbaId}/rfi/${rfiId}`),
    enabled: !!stavbaId && !!rfiId,
  });
}

/** Upravit RFI (PUT /rfi/{id}) — odpověď, stav, atd. */
export function useUpdateRfi(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rfiId, payload }: { rfiId: number; payload: RfiUpdatePayload }) =>
      api.put<Rfi>(`/stavby/${stavbaId}/rfi/${rfiId}`, payload),
    onSuccess: (_data, vars) => {
      if (stavbaId) {
        qc.invalidateQueries({ queryKey: qk.rfi(stavbaId) });
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'rfi', vars.rfiId] });
      }
    },
  });
}

/** Smazat RFI. */
export function useDeleteRfi(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rfiId: number) => api.delete<void>(`/stavby/${stavbaId}/rfi/${rfiId}`),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: qk.rfi(stavbaId) });
    },
  });
}

// === Kontrolní dny ===
export function useKontrolniDny(stavbaId: number | null) {
  return useQuery<KontrolniDen[]>({
    queryKey: stavbaId ? qk.kontrolniDny(stavbaId) : ['kd-inactive'],
    queryFn: () => api.get<KontrolniDen[]>(`/stavby/${stavbaId}/kd`),
    enabled: !!stavbaId,
  });
}

export interface KontrolniDenCreatePayload {
  datum?: string;
  data_json?: Record<string, unknown>;
}

export interface KontrolniDenDetail {
  id: number;
  stavba_id: number;
  cislo: number;
  datum: string;
  stav: string;
  data_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  ukoly_count: number;
  nesplneno_count: number;
  photos_count: number;
  attachments_count: number;
}

/** Detail jednoho KD (GET /stavby/{sid}/kd/{kd_id}). */
export function useKontrolniDen(stavbaId: number | null, kdId: number | null) {
  return useQuery<KontrolniDenDetail>({
    queryKey: stavbaId && kdId ? ['stavby', stavbaId, 'kd', kdId] : ['kd-detail-inactive'],
    queryFn: () => api.get<KontrolniDenDetail>(`/stavby/${stavbaId}/kd/${kdId}`),
    enabled: !!stavbaId && !!kdId,
  });
}

/** Vytvořit nový kontrolní den (POST /stavby/{sid}/kd). */
export function useCreateKontrolniDen(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: KontrolniDenCreatePayload) =>
      api.post<KontrolniDenDetail>(`/stavby/${stavbaId}/kd`, {
        stavba_id: stavbaId,
        data_json: payload.data_json ?? {},
        datum: payload.datum || undefined,
      }),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: qk.kontrolniDny(stavbaId) });
    },
  });
}

/** Update existujícího KD (PUT /stavby/{sid}/kd/{kd_id}). */
export function useUpdateKontrolniDen(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      kdId,
      data_json,
      stav,
      datum,
    }: {
      kdId: number;
      data_json?: Record<string, unknown>;
      stav?: string;
      datum?: string;
    }) =>
      api.put<KontrolniDenDetail>(`/stavby/${stavbaId}/kd/${kdId}`, {
        data_json,
        stav,
        datum,
      }),
    onSuccess: (_data, vars) => {
      if (stavbaId) {
        qc.invalidateQueries({ queryKey: qk.kontrolniDny(stavbaId) });
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'kd', vars.kdId] });
      }
    },
  });
}

/** Nový KD z předchozího (carry-over nesplněných úkolů). */
export function useKdNovyZPredchoziho(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kdId: number) =>
      api.post<KontrolniDenDetail>(`/stavby/${stavbaId}/kd/${kdId}/novy-z-predchoziho`, {}),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: qk.kontrolniDny(stavbaId) });
    },
  });
}

/** Převést vady z KD do evidence vad. */
export function useKdExtractVady(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation<{ created: number; vady_ids: number[] }, unknown, number>({
    mutationFn: (kdId: number) =>
      api.post<{ created: number; vady_ids: number[] }>(
        `/stavby/${stavbaId}/kd/${kdId}/extract-vady`,
        {},
      ),
    onSuccess: () => {
      if (stavbaId) {
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'vady'] });
      }
    },
  });
}

/** Odeslat email notifikaci zodpovědným osobám. Backend sám dohledá úkoly a emaily. */
export function useKdNotify(stavbaId: number | null) {
  return useMutation<{ message?: string; notified?: string[]; skipped?: string[] }, unknown, number>({
    mutationFn: (kdId: number) =>
      api.post(`/stavby/${stavbaId}/kd/${kdId}/notify`, {}),
  });
}

export interface KdUkol {
  id: number;
  kd_id: number;
  typ: string;
  popis: string;
  odpovedny: string;
  termin: string;
  stav: 'novy' | 'rozpracovano' | 'splneno' | string;
  source_kd_id: number | null;
  created_at: string;
  updated_at: string;
}

export type KdUkolStav = 'novy' | 'rozpracovano' | 'splneno';

/** Nesplněné úkoly napříč KD (pro panel „Nesplněné úkoly"). */
export function useKdNesplnene(stavbaId: number | null) {
  return useQuery<KdUkol[]>({
    queryKey: stavbaId ? ['stavby', stavbaId, 'kd', 'nesplnene'] : ['kd-nesplnene-inactive'],
    queryFn: () => api.get<KdUkol[]>(`/stavby/${stavbaId}/kd/ukoly/nesplnene`),
    enabled: !!stavbaId,
  });
}

/** Změnit stav úkolu (novy/rozpracovano/splneno). */
export function useUpdateKdUkol(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kdId, ukolId, stav }: { kdId: number; ukolId: number; stav: KdUkolStav }) =>
      api.put<KdUkol>(`/stavby/${stavbaId}/kd/${kdId}/ukoly/${ukolId}`, { stav }),
    onSuccess: (_data, vars) => {
      if (stavbaId) {
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'kd', 'nesplnene'] });
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'kd', vars.kdId, 'ukoly'] });
        qc.invalidateQueries({ queryKey: qk.kontrolniDny(stavbaId) });
      }
    },
  });
}

// === KD přílohy ===
export interface KdAttachment {
  id: number;
  kd_id: number;
  filename: string;
  mime_type: string;
  size: number;
  created_at: string;
}

export function useKdAttachments(stavbaId: number | null, kdId: number | null) {
  return useQuery<KdAttachment[]>({
    queryKey: stavbaId && kdId ? ['stavby', stavbaId, 'kd', kdId, 'attachments'] : ['kd-att-inactive'],
    queryFn: () => api.get<KdAttachment[]>(`/stavby/${stavbaId}/kd/${kdId}/attachments`),
    enabled: !!stavbaId && !!kdId,
  });
}

export function useKdUploadAttachment(stavbaId: number | null, kdId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.post<KdAttachment>(`/stavby/${stavbaId}/kd/${kdId}/attachments`, form);
    },
    onSuccess: () => {
      if (stavbaId && kdId) {
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'kd', kdId, 'attachments'] });
      }
    },
  });
}

export function useKdDeleteAttachment(stavbaId: number | null, kdId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attId: number) =>
      api.delete<void>(`/stavby/${stavbaId}/kd/${kdId}/attachments/${attId}`),
    onSuccess: () => {
      if (stavbaId && kdId) {
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'kd', kdId, 'attachments'] });
      }
    },
  });
}

// === KD fotky ===
export function useKdPhotos(stavbaId: number | null, kdId: number | null) {
  return useQuery<StavbaPhoto[]>({
    queryKey: stavbaId && kdId ? ['stavby', stavbaId, 'kd', kdId, 'photos'] : ['kd-photos-inactive'],
    queryFn: () => api.get<StavbaPhoto[]>(`/stavby/${stavbaId}/kd/${kdId}/photos`),
    enabled: !!stavbaId && !!kdId,
  });
}

/** Propojit existující fotky stavby s KD (POST photo_ids). */
export function useKdLinkPhotos(stavbaId: number | null, kdId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (photoIds: number[]) =>
      api.post<StavbaPhoto[]>(`/stavby/${stavbaId}/kd/${kdId}/photos`, { photo_ids: photoIds }),
    onSuccess: () => {
      if (stavbaId && kdId) {
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'kd', kdId, 'photos'] });
      }
    },
  });
}

// === KD sdílení (read-only) ===

/** Sdílení, která jsem vytvořil (komu jsem nasdílel své KD). */
export function useKdShares() {
  return useQuery<KdShare[]>({
    queryKey: qk.kdShares,
    queryFn: () => api.get<KdShare[]>(`/kd/shares`),
  });
}

/** Nasdílet KD (celou stavbu nebo jeden KD) jinému účtu jen pro čtení. */
export function useAddKdShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email: string; stavba_id: number; kd_id?: number | null }) =>
      api.post<KdShare>(`/kd/shares`, {
        email: payload.email,
        stavba_id: payload.stavba_id,
        kd_id: payload.kd_id ?? null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.kdShares }),
  });
}

export function useRemoveKdShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shareId: number) => api.delete<void>(`/kd/shares/${shareId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.kdShares }),
  });
}

/** Kontrolní dny nasdílené mně (read-only). */
export function useSharedKd() {
  return useQuery<SharedKd[]>({
    queryKey: qk.kdSharedWithMe,
    queryFn: () => api.get<SharedKd[]>(`/kd/shared-with-me`),
  });
}

// === Templates ===
export function useTemplates(docType?: string) {
  return useQuery<DocTemplate[]>({
    queryKey: qk.templates(docType),
    queryFn: () =>
      api.get<DocTemplate[]>('/templates/', {
        query: docType ? { doc_type: docType } : undefined,
      }),
  });
}

export interface TemplateDetail {
  id: number;
  doc_type: string;
  name: string;
  data_json: string;
  created_at?: string;
  updated_at?: string;
}

export function useTemplate(id: number | null) {
  return useQuery<TemplateDetail>({
    queryKey: id ? ['templates', id] : ['template-inactive'],
    queryFn: () => api.get<TemplateDetail>(`/templates/${id}`),
    enabled: !!id,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { doc_type: string; name: string; data_json: unknown }) =>
      api.post<TemplateDetail>('/templates/', {
        doc_type: payload.doc_type,
        name: payload.name,
        data_json:
          typeof payload.data_json === 'string'
            ? payload.data_json
            : JSON.stringify(payload.data_json),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, data_json }: { id: number; name?: string; data_json?: unknown }) =>
      api.put<TemplateDetail>(`/templates/${id}`, {
        name,
        data_json:
          data_json === undefined
            ? undefined
            : typeof data_json === 'string'
              ? data_json
              : JSON.stringify(data_json),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

// === Dashboard ===
export function useDashboardOverview() {
  return useQuery<DashboardOverview>({
    queryKey: qk.dashboard,
    queryFn: () => api.get<DashboardOverview>('/dashboard/'),
  });
}

// === Harmonogram ===
export function useHarmonogramy(stavbaId: number | null) {
  return useQuery<HarmonogramListItem[]>({
    queryKey: stavbaId ? qk.harmonogramy(stavbaId) : ['harmonogramy-inactive'],
    queryFn: () => api.get<HarmonogramListItem[]>(`/stavby/${stavbaId}/harmonogramy`),
    enabled: !!stavbaId,
  });
}

export function useHarmonogram(stavbaId: number | null, harmonogramId: number | null) {
  return useQuery<Harmonogram>({
    queryKey:
      stavbaId && harmonogramId ? qk.harmonogram(stavbaId, harmonogramId) : ['harmonogram-inactive'],
    queryFn: () => api.get<Harmonogram>(`/stavby/${stavbaId}/harmonogramy/${harmonogramId}`),
    enabled: !!stavbaId && !!harmonogramId,
  });
}

export function useCreateHarmonogram(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: HarmonogramCreate) =>
      api.post<Harmonogram>(`/stavby/${stavbaId}/harmonogramy`, data),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: qk.harmonogramy(stavbaId) });
    },
  });
}

export function useCreateHarmonogramTask(stavbaId: number | null, harmonogramId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: HarmonogramTaskCreate) =>
      api.post<HarmonogramTask>(
        `/stavby/${stavbaId}/harmonogramy/${harmonogramId}/tasks`,
        data,
      ),
    onSuccess: () => {
      if (stavbaId && harmonogramId)
        qc.invalidateQueries({ queryKey: qk.harmonogram(stavbaId, harmonogramId) });
    },
  });
}

// === Documents (archiv uložených dokumentů) ===
export function useDocuments(filters?: { doc_type?: string; stavba?: string }) {
  return useQuery<SavedDocument[]>({
    queryKey: qk.documents(filters as Record<string, string | number | undefined>),
    queryFn: () =>
      api.get<SavedDocument[]>('/documents/', {
        query: filters as Record<string, string | number | undefined> | undefined,
      }),
  });
}

export function useDocument(id: number | null) {
  return useQuery<SavedDocument>({
    queryKey: id ? qk.document(id) : ['document-inactive'],
    queryFn: () => api.get<SavedDocument>(`/documents/${id}`),
    enabled: !!id,
  });
}

export function useSaveDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SavedDocumentCreate & { id?: number }) => {
      const payload: SavedDocumentCreate = {
        doc_type: data.doc_type,
        title: data.title,
        stavba: data.stavba ?? '',
        firma: data.firma ?? '',
        data_json: data.data_json,
      };
      if (data.id) return api.put<SavedDocument>(`/documents/${data.id}`, payload);
      return api.post<SavedDocument>('/documents/', payload);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

// === Photos ===
export function usePhotos(stavbaId: number | null, kategorie?: string) {
  return useQuery<StavbaPhoto[]>({
    queryKey: stavbaId ? qk.photos(stavbaId, kategorie) : ['photos-inactive'],
    queryFn: () =>
      api.get<StavbaPhoto[]>(`/stavby/${stavbaId}/photos`, {
        query: kategorie ? { kategorie } : undefined,
      }),
    enabled: !!stavbaId,
  });
}

export function useUploadPhoto(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { file: File; popis?: string; kategorie?: string; datum?: string }) => {
      const form = new FormData();
      form.append('file', payload.file);
      if (payload.popis) form.append('popis', payload.popis);
      if (payload.kategorie) form.append('kategorie', payload.kategorie);
      if (payload.datum) form.append('datum', payload.datum);
      return api.post<StavbaPhoto>(`/stavby/${stavbaId}/photos`, form);
    },
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'photos'] });
    },
  });
}

export function useUpdatePhoto(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      photoId,
      payload,
    }: {
      photoId: number;
      payload: { popis?: string; kategorie?: string; datum?: string };
    }) => api.put<StavbaPhoto>(`/stavby/${stavbaId}/photos/${photoId}`, payload),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'photos'] });
    },
  });
}

export function useDeletePhoto(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (photoId: number) =>
      api.delete<void>(`/stavby/${stavbaId}/photos/${photoId}`),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'photos'] });
    },
  });
}

/** POST body = pole ID fotek (stejně jako legacy frontend/index.html). */
export function useDeletePhotosBulk(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (photoIds: number[]) =>
      api.post<{ deleted: number }>(`/stavby/${stavbaId}/photos/delete-bulk`, photoIds),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'photos'] });
    },
  });
}

// === Project docs (projektová dokumentace) ===
export function useProjectDocs(stavbaId: number | null, kategorie?: string) {
  return useQuery<ProjectFile[]>({
    queryKey: stavbaId ? qk.projectDocs(stavbaId, kategorie) : ['project-docs-inactive'],
    queryFn: () =>
      api.get<ProjectFile[]>(`/stavby/${stavbaId}/project-docs`, {
        query: kategorie ? { kategorie } : undefined,
      }),
    enabled: !!stavbaId,
  });
}

export function useProjectDocsStats(stavbaId: number | null) {
  return useQuery<ProjectFileStats[]>({
    queryKey: stavbaId ? [...qk.projectDocs(stavbaId), 'stats'] : ['project-docs-stats-inactive'],
    queryFn: () => api.get<ProjectFileStats[]>(`/stavby/${stavbaId}/project-docs/stats`),
    enabled: !!stavbaId,
  });
}

export function useUploadProjectDoc(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { file: File; kategorie?: string; popis?: string }) => {
      const form = new FormData();
      form.append('file', payload.file);
      if (payload.kategorie) form.append('kategorie', payload.kategorie);
      if (payload.popis) form.append('popis', payload.popis);
      return api.post<ProjectFile>(`/stavby/${stavbaId}/project-docs`, form);
    },
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'project-docs'] });
    },
  });
}

export function useDeleteProjectDoc(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fid: number) => api.delete<void>(`/stavby/${stavbaId}/project-docs/${fid}`),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'project-docs'] });
    },
  });
}

// === Sdílení jednotlivých dokumentů ===

/** Seznam sdílení (s uživateli) jednoho souboru. */
export function useProjectDocShares(stavbaId: number | null, fileId: number | null) {
  return useQuery<ProjectFileShare[]>({
    queryKey: ['stavby', stavbaId, 'project-docs', fileId, 'shares'],
    queryFn: () => api.get<ProjectFileShare[]>(`/stavby/${stavbaId}/project-docs/${fileId}/shares`),
    enabled: !!stavbaId && !!fileId,
  });
}

/** Nasdílet soubor uživateli aplikace přes e-mail. */
export function useShareProjectDoc(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { fileId: number; email: string }) =>
      api.post<ProjectFileShare>(`/stavby/${stavbaId}/project-docs/${payload.fileId}/shares`, {
        email: payload.email,
      }),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'project-docs', vars.fileId, 'shares'] }),
  });
}

export function useUnshareProjectDoc(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { fileId: number; shareId: number }) =>
      api.delete<void>(`/stavby/${stavbaId}/project-docs/${payload.fileId}/shares/${payload.shareId}`),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'project-docs', vars.fileId, 'shares'] }),
  });
}

/** Aktuální veřejný odkaz souboru (404 → undefined). */
export function useProjectDocPublicLink(stavbaId: number | null, fileId: number | null) {
  return useQuery<ProjectFilePublicLink | null>({
    queryKey: ['stavby', stavbaId, 'project-docs', fileId, 'public-link'],
    queryFn: async () => {
      try {
        return await api.get<ProjectFilePublicLink>(
          `/stavby/${stavbaId}/project-docs/${fileId}/public-link`,
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: !!stavbaId && !!fileId,
  });
}

export function useCreatePublicLink(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: number) =>
      api.post<ProjectFilePublicLink>(`/stavby/${stavbaId}/project-docs/${fileId}/public-link`),
    onSuccess: (_d, fileId) =>
      qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'project-docs', fileId, 'public-link'] }),
  });
}

export function useDeletePublicLink(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: number) =>
      api.delete<void>(`/stavby/${stavbaId}/project-docs/${fileId}/public-link`),
    onSuccess: (_d, fileId) =>
      qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'project-docs', fileId, 'public-link'] }),
  });
}

/** Dokumenty nasdílené mně (jednotlivé soubory). */
export function useSharedProjectFiles() {
  return useQuery<SharedProjectFile[]>({
    queryKey: ['project-docs', 'shared-files'],
    queryFn: () => api.get<SharedProjectFile[]>(`/project-docs/shared-files`),
  });
}

// === Změnové listy ===
export function useZmenoveListy(stavbaId: number | null, stav?: string) {
  return useQuery<ZmenovyList[]>({
    queryKey: stavbaId ? [...qk.zmenoveListy(stavbaId), stav ?? 'all'] : ['zmenove-listy-inactive'],
    queryFn: () =>
      api.get<ZmenovyList[]>(`/stavby/${stavbaId}/zmenove-listy`, {
        query: stav ? { stav } : undefined,
      }),
    enabled: !!stavbaId,
  });
}

export function useZmenoveListyStats(stavbaId: number | null) {
  return useQuery<ZmenovyListStats[]>({
    queryKey: stavbaId ? qk.zmenoveListyStats(stavbaId) : ['zmenove-listy-stats-inactive'],
    queryFn: () => api.get<ZmenovyListStats[]>(`/stavby/${stavbaId}/zmenove-listy/stats`),
    enabled: !!stavbaId,
  });
}

export function useCreateZmenovyList(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ZmenovyListCreatePayload) =>
      api.post<ZmenovyList>(`/stavby/${stavbaId}/zmenove-listy`, data),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: qk.zmenoveListy(stavbaId) });
    },
  });
}

export function useZmenovyListDetail(stavbaId: number | null, zlId: number | null) {
  return useQuery<ZmenovyList>({
    queryKey: stavbaId && zlId ? ['stavby', stavbaId, 'zl', zlId] : ['zl-inactive'],
    queryFn: () => api.get<ZmenovyList>(`/stavby/${stavbaId}/zmenove-listy/${zlId}`),
    enabled: !!stavbaId && !!zlId,
  });
}

export function useUpdateZmenovyList(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ zlId, payload }: { zlId: number; payload: ZmenovyListUpdatePayload }) =>
      api.put<ZmenovyList>(`/stavby/${stavbaId}/zmenove-listy/${zlId}`, payload),
    onSuccess: (_data, vars) => {
      if (stavbaId) {
        qc.invalidateQueries({ queryKey: qk.zmenoveListy(stavbaId) });
        qc.invalidateQueries({ queryKey: ['stavby', stavbaId, 'zl', vars.zlId] });
      }
    },
  });
}

export function useDeleteZmenovyList(stavbaId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (zlId: number) =>
      api.delete<void>(`/stavby/${stavbaId}/zmenove-listy/${zlId}`),
    onSuccess: () => {
      if (stavbaId) qc.invalidateQueries({ queryKey: qk.zmenoveListy(stavbaId) });
    },
  });
}

// === Vyúčtování (útraty z firemních peněz) ===

/** Moje položky vyúčtování (zaměstnanec, per účet — nezávislé na stavbě). */
export function useVyuctovani() {
  return useQuery<VyuctovaniPolozka[]>({
    queryKey: qk.vyuctovani,
    queryFn: () => api.get<VyuctovaniPolozka[]>(`/vyuctovani`),
  });
}

export function useCreateVyuctovani() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: VyuctovaniPolozkaCreate) =>
      api.post<VyuctovaniPolozka>(`/vyuctovani`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vyuctovani }),
  });
}

export function useUpdateVyuctovani() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: VyuctovaniPolozkaUpdate }) =>
      api.put<VyuctovaniPolozka>(`/vyuctovani/polozka/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vyuctovani }),
  });
}

export function useDeleteVyuctovani() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/vyuctovani/polozka/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vyuctovani }),
  });
}

/** Sdílení mého vyúčtování s účetní (zaměstnanec). */
export function useVyuctovaniShares() {
  return useQuery<VyuctovaniShare[]>({
    queryKey: qk.vyuctovaniShares,
    queryFn: () => api.get<VyuctovaniShare[]>(`/vyuctovani/shares`),
  });
}

export function useAddVyuctovaniShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      api.post<VyuctovaniShare>(`/vyuctovani/shares`, { email }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vyuctovaniShares }),
  });
}

export function useRemoveVyuctovaniShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shareId: number) =>
      api.delete<void>(`/vyuctovani/shares/${shareId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vyuctovaniShares }),
  });
}

/** Listy nasdílené mně (účetní). */
export function useSharedVyuctovani() {
  return useQuery<SharedVyuctovani[]>({
    queryKey: qk.vyuctovaniSharedWithMe,
    queryFn: () => api.get<SharedVyuctovani[]>(`/vyuctovani/shared-with-me`),
  });
}

/** Odfajfkovat „sedí s dokladem" (účetní). */
export function useSetVyuctovaniOvereni() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, overeno, poznamka_ucetni }: { id: number; overeno: boolean; poznamka_ucetni?: string }) =>
      api.patch<VyuctovaniPolozka>(`/vyuctovani/polozka/${id}/overeni`, { overeno, poznamka_ucetni }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe }),
  });
}

/** Odsouhlasit položku (účetní) — po schválení ji zaměstnanec nemůže upravovat. */
export function useSetVyuctovaniSchvaleni() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, schvaleno }: { id: number; schvaleno: boolean }) =>
      api.patch<VyuctovaniPolozka>(`/vyuctovani/polozka/${id}/schvaleni`, { schvaleno }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe }),
  });
}

/** Účetní navrhne / stáhne návrh smazání útraty (potvrzuje zaměstnanec). */
export function useNavrhSmazaniPolozka() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, navrzeno }: { id: number; navrzeno: boolean }) =>
      api.patch<VyuctovaniPolozka>(`/vyuctovani/polozka/${id}/navrh-smazani`, { navrzeno }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe }),
  });
}

/** Zaměstnanec reaguje na návrh smazání útraty — souhlas (smaže) / odmítnutí s důvodem. */
export function useReakceSmazaniPolozka() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, souhlas, duvod }: { id: number; souhlas: boolean; duvod?: string }) =>
      api.patch<{ deleted: boolean }>(`/vyuctovani/polozka/${id}/smazani`, { souhlas, duvod: duvod ?? '' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vyuctovani });
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe });
    },
  });
}

/** Účetní edit položky (PUT funguje pro účetní i po schválení). */
export function useUpdateVyuctovaniUcetni() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: VyuctovaniPolozkaUpdate }) =>
      api.put<VyuctovaniPolozka>(`/vyuctovani/polozka/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe }),
  });
}

/** Zůstatek firemních peněz přihlášeného zaměstnance. */
export function useVyuctovaniSouhrn() {
  return useQuery<VyuctovaniSouhrn>({
    queryKey: qk.vyuctovaniSouhrn,
    queryFn: () => api.get<VyuctovaniSouhrn>('/vyuctovani/souhrn'),
  });
}

// === Notifikace Vyúčtování ===

/** Moje nastavení notifikací (vytvoří default opt-in, pokud ještě není). */
export function useNotifikaceNastaveni() {
  return useQuery<NotifikaceNastaveni>({
    queryKey: qk.notifikaceNastaveni,
    queryFn: () => api.get<NotifikaceNastaveni>('/notifikace/nastaveni'),
  });
}

/** Uložit nastavení notifikací. */
export function useUpdateNotifikaceNastaveni() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: NotifikaceNastaveni) =>
      api.put<NotifikaceNastaveni>('/notifikace/nastaveni', payload),
    onSuccess: (data) => qc.setQueryData(qk.notifikaceNastaveni, data),
  });
}

/** Poslat zkušební notifikaci sobě (podle aktuálního nastavení). */
export function useTestNotifikace() {
  return useMutation({
    mutationFn: () => api.post<{ status: string }>('/notifikace/test'),
  });
}

/** Účetní/admin přidá vklad (firemní peníze) zaměstnanci. */
export function useAddVyuctovaniVklad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ownerId, castka, datum, poznamka }: { ownerId: number; castka: number; datum?: string; poznamka?: string }) =>
      api.post<VyuctovaniVklad>(`/vyuctovani/vklady/${ownerId}`, { castka, datum, poznamka }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe });
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSouhrn });
    },
  });
}

/** Účetní/admin smaže vklad. */
export function useRemoveVyuctovaniVklad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vkladId: number) => api.delete<void>(`/vyuctovani/vklad/${vkladId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe });
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSouhrn });
    },
  });
}

/** Zaměstnanec potvrdí (nebo zruší potvrzení) přijetí vkladu od účetní. */
export function useAcceptVyuctovaniVklad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vkladId, prijato }: { vkladId: number; prijato: boolean }) =>
      api.patch<VyuctovaniVklad>(`/vyuctovani/vklady/${vkladId}/prijeti`, { prijato }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSouhrn });
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe });
    },
  });
}

/** Zaměstnanec odmítne příjem vkladu od účetní s důvodem (zobrazí se účetní). */
export function useRejectVyuctovaniVklad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vkladId, duvod }: { vkladId: number; duvod: string }) =>
      api.patch<VyuctovaniVklad>(`/vyuctovani/vklady/${vkladId}/odmitnuti`, { duvod }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSouhrn });
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe });
    },
  });
}

/** Účetní navrhne / stáhne návrh smazání vkladu (potvrzuje zaměstnanec). */
export function useNavrhSmazaniVklad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vkladId, navrzeno }: { vkladId: number; navrzeno: boolean }) =>
      api.patch<VyuctovaniVklad>(`/vyuctovani/vklad/${vkladId}/navrh-smazani`, { navrzeno }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe });
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSouhrn });
    },
  });
}

/** Zaměstnanec reaguje na návrh smazání vkladu — souhlas (smaže) / odmítnutí s důvodem. */
export function useReakceSmazaniVklad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vkladId, souhlas, duvod }: { vkladId: number; souhlas: boolean; duvod?: string }) =>
      api.patch<{ deleted: boolean }>(`/vyuctovani/vklad/${vkladId}/smazani`, { souhlas, duvod: duvod ?? '' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSouhrn });
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe });
    },
  });
}

// ─── PŘEVODY MEZI KOLEGY ─────────────────────────────────────────────────────

/** Odesílatel předá firemní peníze kolegovi (podle e-mailu). */
export function useCreateVyuctovaniPrevod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: VyuctovaniPrevodCreate) =>
      api.post<VyuctovaniPrevod>(`/vyuctovani/prevody`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSouhrn });
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe });
    },
  });
}

/** Příjemce potvrdí (nebo zruší potvrzení) přijetí převodu od kolegy. */
export function useAcceptVyuctovaniPrevod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ prevodId, prijato }: { prevodId: number; prijato: boolean }) =>
      api.patch<VyuctovaniPrevod>(`/vyuctovani/prevody/${prevodId}/prijeti`, { prijato }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSouhrn });
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe });
    },
  });
}

/** Příjemce odmítne příjem převodu od kolegy s důvodem (uvidí odesílatel i jeho účetní). */
export function useRejectVyuctovaniPrevod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ prevodId, duvod }: { prevodId: number; duvod: string }) =>
      api.patch<VyuctovaniPrevod>(`/vyuctovani/prevody/${prevodId}/odmitnuti`, { duvod }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSouhrn });
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe });
    },
  });
}

/** Odesílatel zruší dosud nepřijatý převod (peníze se mu vrátí). */
export function useCancelVyuctovaniPrevod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prevodId: number) => api.delete<void>(`/vyuctovani/prevody/${prevodId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSouhrn });
      qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe });
    },
  });
}

/** Účetní odfajfkne, že převod viděla / sedí. */
export function useSetVyuctovaniPrevodOvereni() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ prevodId, overeno }: { prevodId: number; overeno: boolean }) =>
      api.patch<VyuctovaniPrevod>(`/vyuctovani/prevody/${prevodId}/overeni`, { overeno }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vyuctovaniSharedWithMe }),
  });
}

// === Karty zaměstnanců (evidence majetku a OOPP) ===

/** Moje karty (tvůrce). */
export function useZamestnanci() {
  return useQuery<ZamestnanecKarta[]>({
    queryKey: qk.zamestnanci,
    queryFn: () => api.get<ZamestnanecKarta[]>(`/zamestnanci`),
  });
}

/** Karty nasdílené mně (zaměstnanec). */
export function useSharedZamestnanci() {
  return useQuery<ZamestnanecKarta[]>({
    queryKey: qk.zamestnanciSharedWithMe,
    queryFn: () => api.get<ZamestnanecKarta[]>(`/zamestnanci/shared-with-me`),
  });
}

export function useCreateZamestnanec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ZamestnanecKartaCreate) =>
      api.post<ZamestnanecKarta>(`/zamestnanci`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.zamestnanci }),
  });
}

export function useUpdateZamestnanec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ZamestnanecKartaUpdate }) =>
      api.put<ZamestnanecKarta>(`/zamestnanci/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.zamestnanci }),
  });
}

export function useDeleteZamestnanec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/zamestnanci/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.zamestnanci }),
  });
}

export function useCreateZamestnanecPolozka() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kartaId, payload }: { kartaId: number; payload: ZamestnanecPolozkaCreate }) =>
      api.post<ZamestnanecPolozka>(`/zamestnanci/${kartaId}/polozky`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.zamestnanci }),
  });
}

export function useUpdateZamestnanecPolozka() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kartaId, polozkaId, payload }: { kartaId: number; polozkaId: number; payload: ZamestnanecPolozkaUpdate }) =>
      api.put<ZamestnanecPolozka>(`/zamestnanci/${kartaId}/polozky/${polozkaId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.zamestnanci }),
  });
}

export function useDeleteZamestnanecPolozka() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kartaId, polozkaId }: { kartaId: number; polozkaId: number }) =>
      api.delete<void>(`/zamestnanci/${kartaId}/polozky/${polozkaId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.zamestnanci }),
  });
}

/** Potvrzení převzetí položky (zaměstnanec i tvůrce). */
export function useAcceptZamestnanecPolozka() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ polozkaId, prijato }: { polozkaId: number; prijato: boolean }) =>
      api.patch<ZamestnanecPolozka>(`/zamestnanci/polozky/${polozkaId}/prijeti`, { prijato }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.zamestnanci });
      qc.invalidateQueries({ queryKey: qk.zamestnanciSharedWithMe });
    },
  });
}

/** Sdílení karty se zaměstnancem. */
export function useZamestnanecShares(kartaId: number | null) {
  return useQuery<ZamestnanecKartaShare[]>({
    queryKey: kartaId ? qk.zamestnanecShares(kartaId) : ['zamestnanec-shares-inactive'],
    queryFn: () => api.get<ZamestnanecKartaShare[]>(`/zamestnanci/${kartaId}/shares`),
    enabled: !!kartaId,
  });
}

export function useAddZamestnanecShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kartaId, email }: { kartaId: number; email: string }) =>
      api.post<ZamestnanecKartaShare>(`/zamestnanci/${kartaId}/shares`, { email }),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: qk.zamestnanecShares(vars.kartaId) }),
  });
}

export function useRemoveZamestnanecShare() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kartaId, shareId }: { kartaId: number; shareId: number }) =>
      api.delete<void>(`/zamestnanci/${kartaId}/shares/${shareId}`),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: qk.zamestnanecShares(vars.kartaId) }),
  });
}

// ─── ÚKOLY (delegování) ──────────────────────────────────────────────────────

function invalidateUkoly(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: qk.ukolyZadane });
  qc.invalidateQueries({ queryKey: qk.ukolyMoje });
}

/** Úkoly, které jsem zadal (jsem zadavatel). */
export function useUkolyZadane() {
  return useQuery<Ukol[]>({
    queryKey: qk.ukolyZadane,
    queryFn: () => api.get<Ukol[]>('/ukoly/zadane'),
  });
}

/** Úkoly přiřazené mně (jsem řešitel). */
export function useUkolyMoje() {
  return useQuery<Ukol[]>({
    queryKey: qk.ukolyMoje,
    queryFn: () => api.get<Ukol[]>('/ukoly/moje'),
  });
}

/** Vytvořit nový úkol a přiřadit ho přes e-mail řešitele. */
export function useCreateUkol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UkolCreate) => api.post<Ukol>('/ukoly', payload),
    onSuccess: () => invalidateUkoly(qc),
  });
}

/** Editace úkolu (jen zadavatel). */
export function useUpdateUkol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UkolUpdate }) =>
      api.put<Ukol>(`/ukoly/${id}`, payload),
    onSuccess: () => invalidateUkoly(qc),
  });
}

/** Smazat úkol (jen zadavatel). */
export function useDeleteUkol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/ukoly/${id}`),
    onSuccess: () => invalidateUkoly(qc),
  });
}

/** Změna stavu úkolu (+ slovní výstup při splnění). */
export function useSetUkolStav() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stav, vystupText }: { id: number; stav: UkolStav; vystupText?: string }) =>
      api.patch<Ukol>(`/ukoly/${id}/stav`, { stav, vystup_text: vystupText }),
    onSuccess: () => invalidateUkoly(qc),
  });
}

/** Nahrát výstup (PDF, dokument…) k úkolu. */
export function useUploadUkolVystup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      return api.post<Ukol>(`/ukoly/${id}/vystup`, form);
    },
    onSuccess: () => invalidateUkoly(qc),
  });
}

/** Smazat nahraný výstup. */
export function useDeleteUkolVystup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, attId }: { id: number; attId: number }) =>
      api.delete<void>(`/ukoly/${id}/vystup/${attId}`),
    onSuccess: () => invalidateUkoly(qc),
  });
}

// ─── NÁSTĚNKA (osobní poznámky) ──────────────────────────────────────────────

/** Moje kartičky na nástěnce. */
export function usePoznamky() {
  return useQuery<PoznamkaKarta[]>({
    queryKey: qk.poznamky,
    queryFn: () => api.get<PoznamkaKarta[]>('/poznamky'),
  });
}

/** Nová kartička. */
export function useCreatePoznamka() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PoznamkaKartaCreate) => api.post<PoznamkaKarta>('/poznamky', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.poznamky }),
  });
}

/** Editace kartičky (částečná — text, barva, připnutí, hotovo…). */
export function useUpdatePoznamka() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: PoznamkaKartaUpdate }) =>
      api.put<PoznamkaKarta>(`/poznamky/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.poznamky }),
  });
}

/** Smazat kartičku. */
export function useDeletePoznamka() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/poznamky/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.poznamky }),
  });
}

/** Uložit nové pořadí kartiček (drag & drop). */
export function useReorderPoznamky() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => api.put<void>('/poznamky/reorder', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.poznamky }),
  });
}

// ─── KNIHA JÍZD (vozidla + měsíční záznamy) ──────────────────────────────────

/** Vozidla přihlášeného uživatele. */
export function useVehicles() {
  return useQuery<Vehicle[]>({
    queryKey: qk.vehicles,
    queryFn: () => api.get<Vehicle[]>('/vehicles'),
  });
}

export function useCreateVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: VehicleCreate) => api.post<Vehicle>('/vehicles', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vehicles }),
  });
}

export function useUpdateVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: VehicleUpdate }) =>
      api.put<Vehicle>(`/vehicles/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vehicles }),
  });
}

export function useDeleteVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/vehicles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.vehicles }),
  });
}

/** Přehled měsíců vozidla (se součty), nejnovější první. */
export function useLogbookMonths(vehicleId: number | null) {
  return useQuery<LogbookMonthListItem[]>({
    queryKey: vehicleId ? qk.logbookMonths(vehicleId) : ['logbook-months-inactive'],
    queryFn: () => api.get<LogbookMonthListItem[]>(`/vehicles/${vehicleId}/months`),
    enabled: !!vehicleId,
  });
}

/** Detail jednoho měsíce včetně řádků jízd. */
export function useLogbookMonth(
  vehicleId: number | null,
  year: number | null,
  month: number | null,
) {
  return useQuery<LogbookMonth>({
    queryKey:
      vehicleId && year && month
        ? qk.logbookMonth(vehicleId, year, month)
        : ['logbook-month-inactive'],
    queryFn: () => api.get<LogbookMonth>(`/vehicles/${vehicleId}/months/${year}/${month}`),
    enabled: !!vehicleId && !!year && !!month,
  });
}

/** Vytvořit nový měsíc (409 = už existuje). */
export function useCreateLogbookMonth(vehicleId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LogbookMonthCreate) =>
      api.post<LogbookMonth>(`/vehicles/${vehicleId}/months`, payload),
    onSuccess: () => {
      if (vehicleId) qc.invalidateQueries({ queryKey: qk.logbookMonths(vehicleId) });
    },
  });
}

/** Uložit měsíc — počáteční km, řidič, bulk replace řádků. */
export function useUpdateLogbookMonth(vehicleId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      year,
      month,
      payload,
    }: {
      year: number;
      month: number;
      payload: LogbookMonthUpdate;
    }) => api.put<LogbookMonth>(`/vehicles/${vehicleId}/months/${year}/${month}`, payload),
    onSuccess: (_data, vars) => {
      if (vehicleId) {
        qc.invalidateQueries({ queryKey: qk.logbookMonths(vehicleId) });
        qc.invalidateQueries({ queryKey: qk.logbookMonth(vehicleId, vars.year, vars.month) });
      }
    },
  });
}

export function useDeleteLogbookMonth(vehicleId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) =>
      api.delete<void>(`/vehicles/${vehicleId}/months/${year}/${month}`),
    onSuccess: () => {
      if (vehicleId) qc.invalidateQueries({ queryKey: qk.logbookMonths(vehicleId) });
    },
  });
}

// === Logo firmy (knihovna log) ===
export function useLogos() {
  return useQuery<Logo[]>({
    queryKey: qk.logos,
    queryFn: () => api.get<Logo[]>('/logos'),
  });
}

/** Aktivní logo uživatele (nebo null). Použito v hlavičce dokumentů + PDF. */
export function useActiveLogo() {
  return useQuery<Logo | null>({
    queryKey: qk.activeLogo,
    queryFn: () => api.get<Logo | null>('/logos/active'),
    staleTime: 5 * 60_000,
  });
}

function invalidateLogos(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: qk.logos });
  qc.invalidateQueries({ queryKey: qk.activeLogo });
}

export function useCreateLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: LogoCreate) => api.post<Logo>('/logos', data),
    onSuccess: () => invalidateLogos(qc),
  });
}

export function useActivateLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (logoId: number) => api.put<Logo>(`/logos/${logoId}/activate`),
    onSuccess: () => invalidateLogos(qc),
  });
}

export function useDeleteLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (logoId: number) => api.delete<void>(`/logos/${logoId}`),
    onSuccess: () => invalidateLogos(qc),
  });
}

/** Zruší aktivní logo (žádné se nezobrazí na dokumentech). */
export function useDeactivateLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>('/logos/active'),
    onSuccess: () => invalidateLogos(qc),
  });
}

// === Knihovna podpisů ===
export function usePodpisy(enabled = true) {
  return useQuery<Podpis[]>({
    queryKey: qk.podpisy,
    queryFn: () => api.get<Podpis[]>('/podpisy'),
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useCreatePodpis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PodpisCreate) => api.post<Podpis>('/podpisy', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.podpisy }),
  });
}

export function useSetDefaultPodpis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.put<Podpis>(`/podpisy/${id}/default`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.podpisy }),
  });
}

export function useDeletePodpis() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/podpisy/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.podpisy }),
  });
}

// === Podepsané dokumenty (nahrané PDF/JPG podepsané v aplikaci) ===
export function usePodepsaneDokumenty() {
  return useQuery<PodepsanyDokument[]>({
    queryKey: qk.podepsane,
    queryFn: () => api.get<PodepsanyDokument[]>('/podepsane-dokumenty'),
  });
}

export function useCreatePodepsany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { original: File; signed?: Blob; nazev: string; stavbaId?: number | null }) => {
      const form = new FormData();
      form.append('original', p.original, p.original.name);
      if (p.signed) form.append('signed', p.signed, 'podepsano.pdf');
      form.append('nazev', p.nazev);
      if (p.stavbaId) form.append('stavba_id', String(p.stavbaId));
      return api.post<PodepsanyDokument>('/podepsane-dokumenty', form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.podepsane }),
  });
}

export function useUpdatePodepsany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: number; signed: Blob; nazev?: string }) => {
      const form = new FormData();
      form.append('signed', p.signed, 'podepsano.pdf');
      if (p.nazev) form.append('nazev', p.nazev);
      return api.put<PodepsanyDokument>(`/podepsane-dokumenty/${p.id}/signed`, form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.podepsane }),
  });
}

export function useDeletePodepsany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/podepsane-dokumenty/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.podepsane }),
  });
}

// === Košilka — schvalování ===

/** Žádosti o schválení košilky určené mně (jako schvalovateli). */
export function useKosilkyKeSchvaleni() {
  return useQuery<KosilkaSchvaleni[]>({
    queryKey: qk.kosilkyKeSchvaleni,
    queryFn: () => api.get<KosilkaSchvaleni[]>('/kosilky/ke-schvaleni'),
  });
}

/** Mé odeslané žádosti o schválení (pro zobrazení stavu). */
export function useKosilkyOdeslane() {
  return useQuery<KosilkaSchvaleni[]>({
    queryKey: qk.kosilkyOdeslane,
    queryFn: () => api.get<KosilkaSchvaleni[]>('/kosilky/odeslane'),
  });
}

/** Našeptávač dříve použitých e-mailů schvalovatelů. */
export function useKosilkySchvalovatele() {
  return useQuery<ApproverEmail[]>({
    queryKey: qk.kosilkySchvalovatele,
    queryFn: () => api.get<ApproverEmail[]>('/kosilky/schvalovatele'),
  });
}

/** Odeslat košilku ke schválení kolegovi (podle e-mailu). */
export function useCreateKosilkaSchvaleni() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: KosilkaSchvaleniCreate) =>
      api.post<KosilkaSchvaleni>('/kosilky/schvaleni', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.kosilkyOdeslane });
      qc.invalidateQueries({ queryKey: qk.kosilkySchvalovatele });
    },
  });
}

/** Schvalovatel rozhodne (odsouhlasí / zamítne) o košilce. */
export function useDecideKosilkaSchvaleni() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stav, komentar }: { id: number; stav: 'odsouhlaseno' | 'zamitnuto'; komentar?: string }) =>
      api.patch<KosilkaSchvaleni>(`/kosilky/schvaleni/${id}`, { stav, komentar: komentar ?? '' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.kosilkyKeSchvaleni }),
  });
}

/** Odesílatel zruší nevyřízenou žádost o schválení. */
export function useCancelKosilkaSchvaleni() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/kosilky/schvaleni/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.kosilkyOdeslane }),
  });
}
